import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { namespace } from '../../../config.json';
import getConfig from '../utils/Config';

// Components
import PageHeader from '../components/PageHeader';
import LoadingSpinner from '../components/LoadingSpinner';
import MetricsSummary from '../components/MetricsSummary';
import MetricsAPI from '../utils/MetricsAPI';
import { downsampledMetricsAlert } from '../utils/metricsUtils';
import metricsTools from '../utils/metricsTools';
import TimerangeSelector from '../utils/TimerangeSelector';

// Charts
import AreaChartComponent from '../components/charts/StorageAreaChartComponent';
import IOLineChartComponent from '../components/charts/IOLineChartComponent';
import PieChart from '../components/charts/PieChartComponent';
import OperationsHistoryChart from '../components/charts/OperationsHistoryChartComponent';

// Styling
import brandingStyles from '../branding/brandingStyles';
const config = require('../branding/branding.json');
if (config.darkUICharts) {
    require('../components/charts/DarkCharts.css');
} else {
    require('../components/charts/LightCharts.css');
}


// SpecificAccountMetrics Component
class SpecificAccountMetrics extends Component {

    constructor() {
        super();
        const startDate
        = metricsTools.momentToStart(moment().subtract(6, 'days'));
        const endDate = metricsTools.momentToEnd(moment());
        this.state = {
            accountId: null,
            startDate,
            endDate,
            interval: 'days',
            data: null,
            dataLoading: false,
            historyData: null,
            historyDataLoading: false,
            maxUtapiDataPoints: 0,
            downsampledMetrics: false,
            downsampledMetricsMessage: '',
        };
        this.setDates = this.setDates.bind(this);
        this.setStateInterval = this.setStateInterval.bind(this);
        this.refresh = this.refresh.bind(this);
        this.getValidInterval = this.getValidInterval.bind(this);
        this.mounted = false;
        this.metrics = new MetricsAPI();
    }

    componentDidMount() {
        this.mounted = true;
        getConfig().then((config) => {
            this.setState(
                { maxUtapiDataPoints: config.configData.maxUtapiDataPoints }
            );

            this.refresh();
        });
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    refresh() {
        if (this.mounted) {
            this.setState({ dataLoading: true, historyDataLoading: true });
        }
        this.metrics.getAccountMetrics(
            { accountId: this.props.match.params.id, accountName: this.props.match.params.name },
            this.state.startDate,
            this.state.endDate
        )
        .then(res => {
            if (this.mounted) {
                this.setState({
                    data: res.metrics,
                    dataLoading: false,
                    downsampledMetrics: res.downsampledMetrics,
                    downsampledMetricsMessage: res.downsampledMetricsMessage,
                });
            }
        }, res => {
            if (res.status === 401 || res.status === 403) {
                window.location.href =
                    `${namespace}/login/error/${res.status}`;
            }
            if (this.mounted) {
                this.setState({ dataLoading: false });
            }
        });

        this.metrics.getAccountMetricsHistory(
          { accountId: this.props.match.params.id, accountName: this.props.match.params.name },
          this.state.interval,
          this.state.startDate,
          this.state.endDate
        )
        .then(res => {
            if (this.mounted) {
                this.setState({
                    historyData: res.metrics,
                    historyDataLoading: false,
                    downsampledMetrics: res.downsampledMetrics,
                    downsampledMetricsMessage: res.downsampledMetricsMessage,
                });
            }
        }, res => {
            if (res.status === 401 || res.status === 403) {
                window.location.href = `${namespace}/login/error/${res.status}`;
            }
            if (this.mounted) {
                this.setState({ historyDataLoading: false });
            }
        });
    }

    areIntervalAndRangeValid(
      startDate = this.state.startDate,
      endDate = this.state.endDate,
      interval = this.state.interval) {
        const duration = endDate.diff(startDate, interval);
        return (duration < this.state.maxUtapiDataPoints);
    }

    getValidInterval() {
        if (
            this.areIntervalAndRangeValid(
            this.state.startDate,
            this.state.endDate,
            'days')
        ) {
            return 'days';
        }
        return 'months';
    }

    downsizeInterval() {
        if (this.mounted) {
            this.setState(
              { interval: this.getValidInterval(), downsizedInterval: true },
              () => {
                  this.refresh();
              }
            );
        }
    }

    setDates(startDate, endDate) {
        if (this.mounted) {
            this.setState({
                startDate: metricsTools.momentToStart(startDate),
                endDate: metricsTools.momentToEnd(endDate),
            }, () => {
                if (this.areIntervalAndRangeValid()) {
                    this.refresh();
                    this.setState({ downsizedInterval: false });
                } else {
                    this.downsizeInterval();
                }
            });
        }
    }

    setStateInterval(interval) {
        if (this.mounted) {
            this.setState({ interval }, () => {
                if (this.areIntervalAndRangeValid()) {
                    this.refresh();
                    this.setState({ downsizedInterval: false });
                } else {
                    this.downsizeInterval();
                }
            });
        }
    }


    render() {
        const alertInterval = (
          <div className="alert alert-warning">
            The interval has been changed to&nbsp;
            <strong>{this.state.interval}</strong> to display the charts.
          </div>
        );

        const metricsSummary = (
          this.state.dataLoading ?
          <LoadingSpinner /> :
            <MetricsSummary
              storage={
                (this.state.data
                  && this.state.data.storageUtilized
                )
                || []
              }
              quota={parseInt(this.props.match.params.quota, 10)}
              objects={
                (this.state.data
                  && this.state.data.numberOfObjects
                )
                || []
              }
              inThroughput={
                (this.state.data && this.state.data.incomingBytes) || 0
              }
              outThroughput={
                (this.state.data && this.state.data.outgoingBytes) || 0
              }
              operations={
                (this.state.data && this.state.data.operations) || {}
              }
            />
        );

        const storageChart = (
          this.state.historyDataLoading ?
          <LoadingSpinner /> :
            <AreaChartComponent data={
              this.state.historyData || []
              }
              quota={parseInt(this.props.match.params.quota, 10)}
            />
        );

        const ioChart = (
          this.state.historyDataLoading ?
          <LoadingSpinner /> :
            <IOLineChartComponent data={
                this.state.historyData || []
              }
            />
        );

        const opTypesHistory = (
          this.state.historyDataLoading ?
          <LoadingSpinner /> :
            <OperationsHistoryChart
              data={this.state.historyData || []}
              horizontal={false}
            />
        );

        const opTypesSummary = (
          this.state.historyDataLoading ?
          <LoadingSpinner /> :
            <PieChart
              title={'Operations type'}
              legend={false}
              labels={false}
              donut={true}
              data={
                this.state.data && this.state.data.operations || {}
              }
            />
        );

        return (<div
          id="page-wrapper"
          style={brandingStyles.backgrounds.main}
        >
          <PageHeader
            title={`${this.props.match.params.name} metrics`}
            facode="bar-chart"
          />
          <TimerangeSelector
            startDate={this.state.startDate}
            endDate={this.state.endDate}
            setDatesFunc={this.setDates}
            interval={this.state.interval}
            setIntervalFunc={this.setStateInterval}
            isInvalidDate={date => date.diff(moment(), 'days') > 0}
          />
          <br/>
          {this.state.downsizedInterval && alertInterval}
          {this.state.downsampledMetrics && downsampledMetricsAlert(this.state.downsampledMetricsMessage)}
          <div className="row">
            <div className="col-md-12">
              {metricsSummary}
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="panel panel-default"
                style={brandingStyles.backgrounds.panel}>
                <div className="panel-heading"
                  style={brandingStyles.backgrounds.panelHeader}>
                  <i
                    className="fa fa-line-chart"
                    style={brandingStyles.mainColor}
                  />
                  <span>&nbsp;</span>
                  Storage and Bandwidth History
                </div>
                <div className="panel-body">
                  {storageChart}
                  {ioChart}
                </div>
              </div>
            </div>
          </div>

          <div className="row">

            <div className="col-md-8">
              <div className="panel panel-default"
                style={brandingStyles.backgrounds.panel}
              >
                <div className="panel-heading"
                  style={brandingStyles.backgrounds.panelHeader}
                >
                  <i
                    className="fa fa-bar-chart"
                    style={brandingStyles.mainColor}
                  />
                  <span>&nbsp;</span>
                  Operations Types History
                </div>
                <div className="panel-body">
                  {opTypesHistory}
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="panel panel-default"
                style={brandingStyles.backgrounds.panel}
              >
                <div className="panel-heading"
                  style={brandingStyles.backgrounds.panelHeader}
                >
                  <i
                    className="fa fa-pie-chart"
                    style={brandingStyles.mainColor}
                  />
                  <span>&nbsp;</span>
                  Operations Types Summary
                </div>
                <div className="panel-body">
                  {opTypesSummary}
                </div>
              </div>
            </div>


          </div>


        </div>);
    }

}

SpecificAccountMetrics.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            quota: PropTypes.string.isRequired,
        }),
    }),
};

export default SpecificAccountMetrics;



/** WEBPACK FOOTER **
 ** ./src/pages/SpecificAccountMetrics.js
 **/